import { useEffect, useState, useCallback } from "react";
import EditorForTitle from "../tools/EditorForTitle";
import Swal from "sweetalert2";

import {
  getFiltersWithItems,
  createItemInFilter,
  deleteItemInFilter,
  editItemPosition,
  getPresignedUrl,
} from "../../server/Filter";

import { DndContext, closestCenter, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Search, Trash2 } from "lucide-react";
import { colors } from "../../constants/colorConstants.js";

const DEFAULT_FILTERS = {
  brand: [{ title: "hamazon-brand", items: [] }],
  kashrut: [{ title: "hamazon-kashrut", items: [] }],
  "sub-brand": [{ title: "hamazon-sub-brand", items: [] }],
};

const mapTenantKindTitle = {
  brand: "hamazon-brand",
  kashrut: "hamazon-kashrut",
  "sub-brand": "hamazon-sub-brand",
  other: "hamazon-other",
};

function groupByTenantKind(arr) {
  const grouped = arr.reduce((acc, item) => {
    const key = item.tenantKind || "other";
    const title = mapTenantKindTitle[key] || key;
    if (!acc[title]) acc[title] = { title, items: [] };
    acc[title].items.push(item);
    return acc;
  }, {});
  return Object.values(grouped);
}

const filterItemsBySearch = (items, searchTerm) => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return items;
  return items.filter((item) =>
    String(item.name || "")
      .toLowerCase()
      .includes(query),
  );
};

const sortItemsByName = (items, direction) => {
  const sorted = [...items];
  sorted.sort((a, b) => {
    const comparison = String(a.name || "").localeCompare(
      String(b.name || ""),
      "he",
      { numeric: true, sensitivity: "base" },
    );
    return direction === "asc" ? comparison : -comparison;
  });
  return sorted;
};

const getCategoryDisplayItems = (
  items,
  categoryTitle,
  sortByCategory,
  searchTerm,
  { applySearch = true } = {},
) => {
  let displayItems = items;
  const sortDirection = sortByCategory[categoryTitle];
  if (sortDirection) {
    displayItems = sortItemsByName(displayItems, sortDirection);
  }
  if (applySearch) {
    displayItems = filterItemsBySearch(displayItems, searchTerm);
  }
  return displayItems;
};

const Filters = () => {
  const [brand, setBrand] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [sortByCategory, setSortByCategory] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAll = useCallback(async () => {
    try {
      const [brandsRes, kashrutRes, subBrandRes] = await Promise.all([
        getFiltersWithItems("brand"),
        getFiltersWithItems("kashrut"),
        getFiltersWithItems("sub-brand"),
      ]);

      const groupedBrands = groupByTenantKind(brandsRes.data);
      const groupedKashrut = groupByTenantKind(kashrutRes.data);
      const groupedSubBrand = groupByTenantKind(subBrandRes.data);

      setBrand({
        brand: groupedBrands.length ? groupedBrands : DEFAULT_FILTERS.brand,
        kashrut: groupedKashrut.length
          ? groupedKashrut
          : DEFAULT_FILTERS.kashrut,
        "sub-brand": groupedSubBrand.length
          ? groupedSubBrand
          : DEFAULT_FILTERS["sub-brand"],
      });
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const deleteItem = async (item, category) => {
    const parameterId = item?.id || item?.Id;
    const title = category?.title || "brand";

    if (!parameterId) {
      console.error("Missing parameterId", { item });
      return;
    }

    const result = await Swal.fire({
      title: "האם אתה בטוח?",
      text: "לא ניתן לשחזר פריט זה!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.primary,
      cancelButtonColor: colors.danger,
      confirmButtonText: "מחק",
      cancelButtonText: "בטל",
    });

    if (result.isConfirmed) {
      try {
        const kind = title.replace("hamazon-", "");
        await deleteItemInFilter({ id: parameterId }, kind);
        await fetchAll();
        Swal.fire("נמחק בהצלחה!", "", "success");
      } catch (err) {
        console.error(err);
        Swal.fire("שגיאה", "מחיקת הפריט נכשלה", "error");
      }
    }
  };

  const onDragStart = (event) => {
    setActiveItem(event.active.data.current ?? null);
  };

  const onDragEnd = async (event) => {
    setActiveItem(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    if (active.data.current?.categoryTitle !== over.data.current?.categoryTitle)
      return;

    const { categoryTitle, kindKey } = active.data.current;

    const category = brand[kindKey]?.find((cat) => cat.title === categoryTitle);
    if (!category) return;

    // Reorder against the list the user sees; search hides items so drag is disabled then.
    if (searchTerm.trim()) return;

    const displayItems = getCategoryDisplayItems(
      category.items,
      categoryTitle,
      sortByCategory,
      searchTerm,
      { applySearch: false },
    );
    const oldIndex = displayItems.findIndex((item) => item.id === active.id);
    const newIndex = displayItems.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...displayItems];
    const [movedItem] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, movedItem);

    setBrand((prev) => ({
      ...prev,
      [kindKey]: prev[kindKey].map((cat) =>
        cat.title === categoryTitle ? { ...cat, items: reordered } : cat,
      ),
    }));

    setSortByCategory((prev) => {
      const next = { ...prev };
      delete next[categoryTitle];
      return next;
    });

    let position = {};
    if (newIndex === 0) {
      position = { kind: "prepend" };
    } else if (newIndex === reordered.length - 1) {
      position = { kind: "append" };
    } else {
      position = {
        kind: "betweenByRank",
        before: reordered[newIndex - 1].rank,
        after: reordered[newIndex + 1].rank,
      };
    }

    await editItemPosition(categoryTitle, movedItem.id, position);
  };

  const categoryTitlesHebrew = {
    "hamazon-brand": "ספק",
    "hamazon-kashrut": "כשרות",
    "hamazon-sub-brand": "מותג",
    "hamazon-other": "אחר",
  };

  const toggleCategorySort = (categoryTitle) => {
    setSortByCategory((prev) => {
      const current = prev[categoryTitle];
      const direction = current === "asc" ? "desc" : "asc";
      return { ...prev, [categoryTitle]: direction };
    });
  };

  const renderSortIndicator = (categoryTitle) => {
    const direction = sortByCategory[categoryTitle];
    if (!direction) return "";
    return direction === "asc" ? "▲" : "▼";
  };

  if (!brand || Object.keys(brand).length === 0) return <div>Loading...</div>;

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="filters">
        <h1 className="title-role">סינון</h1>
        <div className="filters-toolbar">
          <div className="filters-search">
            <Search aria-hidden size={20} strokeWidth={2} />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="חפש במסננים"
              aria-label="חיפוש במסננים"
            />
          </div>
        </div>
        <div className="flex-container filter-container">
          {Object.entries(brand).map(([kindKey, categories]) => (
            <div key={kindKey} className="category-group">
              {categories.map((category) => {
                const sortDirection = sortByCategory[category.title];
                const displayItems = getCategoryDisplayItems(
                  category.items,
                  category.title,
                  sortByCategory,
                  searchTerm,
                );
                const dragDisabled = Boolean(searchTerm.trim());

                if (searchTerm.trim() && displayItems.length === 0) {
                  return null;
                }

                return (
                  <div key={category.title} className="col-lg-4 filter-column">
                    <div className="filter-column-scroll">
                      <div className="filter-column-toolbar">
                        <button
                          type="button"
                          className={`category-title sort-header-btn${
                            sortDirection ? " sort-header-btn--active" : ""
                          }`}
                          onClick={() => toggleCategorySort(category.title)}
                        >
                          <span>
                            {categoryTitlesHebrew[category.title] ||
                              category.title}
                          </span>
                          <span className="sort-indicator">
                            {renderSortIndicator(category.title)}
                          </span>
                        </button>
                        <div className="filter-column-add">
                          <AddFilterPopup
                            kind={category.title}
                            onAdd={(newItem) => {
                              setBrand((prev) => ({
                                ...prev,
                                [kindKey]: prev[kindKey].map((c) =>
                                  c.title === category.title
                                    ? { ...c, items: [...c.items, newItem] }
                                    : c,
                                ),
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <SortableContext
                        items={displayItems.map((i) => i.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <ul>
                          {displayItems?.length > 0 ? (
                            displayItems.map((item) => (
                              <SortableItem
                                key={item.id}
                                item={item}
                                category={category}
                                kindKey={kindKey}
                                onDelete={deleteItem}
                                dragDisabled={dragDisabled}
                              />
                            ))
                          ) : (
                            <li>
                              {searchTerm.trim()
                                ? "לא נמצאו תוצאות"
                                : "אין פריטים להצגה"}
                            </li>
                          )}
                        </ul>
                      </SortableContext>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* הצגת פריט צף בזמן גרירה */}
      <DragOverlay>
        {activeItem ? (
          <div
            style={{
              background: "var(--hz-surface)",
              border:
                "1px solid var(--hz-cta)" /* TODO: was var(--c-filter-teal) — add to tokens */,
              borderRadius: 8,
              padding: "6px 12px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.13)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              opacity: 0.97,
              cursor: "grabbing",
            }}
          >
            <GripVertical size={18} color="var(--hz-muted)" />
            <span style={{ fontSize: 16 }}>{activeItem.itemName}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default Filters;

const AddFilterPopup = ({ kind, onAdd }) => {
  const handleAddClick = async () => {
    let selectedFile = null;

    const { value: itemName, isConfirmed } = await Swal.fire({
      title: "הוסף פריט חדש",
      input: "text",
      inputLabel: "שם הפריט",
      showCancelButton: true,
      confirmButtonText: "הוסף",
      cancelButtonText: "בטל",
      preConfirm: (value) => {
        if (!value) {
          Swal.showValidationMessage("יש להזין שם פריט!");
          return null;
        }
        return value;
      },
      didOpen: () => {
        if (kind.includes("kashrut")) {
          const container = Swal.getHtmlContainer();
          if (!container) return;

          const fileInput = document.createElement("input");
          fileInput.type = "file";
          fileInput.style.display = "block";
          fileInput.style.marginTop = "10px";

          fileInput.onchange = (e) => {
            selectedFile = e.target.files?.[0] ?? null;
          };

          container.appendChild(fileInput);
        }
      },
    });

    if (!isConfirmed) return;

    const payload = { name: itemName };

    if (kind.includes("kashrut") && selectedFile) {
      const presignRes = await getPresignedUrl(selectedFile, "kashrut-images");
      const uploadUrl = presignRes.data.uploadUrl;

      const fullurl = await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });

      const url = fullurl.url;
      const lastPart = url.split("/").pop();
      const fileName = lastPart.split("?")[0];
      payload.img = fileName;
    }

    try {
      const res = await createItemInFilter(kind, payload);
      if (onAdd) onAdd(res.data);
      Swal.fire("נוסף בהצלחה!", "", "success");
    } catch (err) {
      console.error(err);
      Swal.fire("שגיאה", "הוספת הפריט נכשלה", "error");
    }
  };

  return (
    <button type="button" className="btn" onClick={handleAddClick}>
      הוסף פריט
    </button>
  );
};

const SortableItem = ({
  item,
  category,
  kindKey,
  onDelete,
  dragDisabled = false,
}) => {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: dragDisabled,
    data: {
      kindKey,
      categoryTitle: category.title,
      itemName: item.name,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    touchAction: "none",
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div className="sub-title">
        <div
          className="drag-handle"
          {...listeners}
          {...attributes}
          style={{
            cursor: dragDisabled
              ? "not-allowed"
              : isDragging
                ? "grabbing"
                : "grab",
            display: "flex",
            alignItems: "center",
            padding: "0 4px",
            color: "var(--hz-muted)",
            flexShrink: 0,
            opacity: dragDisabled ? 0.4 : 1,
          }}
          title={
            dragDisabled
              ? "נקה חיפוש כדי לשנות סדר"
              : "גרור לשינוי סדר"
          }
        >
          <GripVertical size={20} strokeWidth={2} />
        </div>

        <EditorForTitle
          kind={category.title}
          id={item.id}
          element={item}
          name="name"
          dist="filters"
        />

        <div
          className="delete-item"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item, category);
          }}
        >
          <div className="img">
            <Trash2 aria-hidden size={20} strokeWidth={2} />
          </div>
        </div>
      </div>
    </li>
  );
};
